import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { get, post } from "@/lib/api";
import { PageLoader, PageHeader, EmptyState, Badge } from "@/components/common";
import { useAccess } from "@/lib/access";
import { ClipboardList, Clock, ListChecks, Trophy, Lock, Zap } from "lucide-react";

export default function Pyp() {
  const nav = useNavigate();
  const [year, setYear] = useState("all");
  const { data, isLoading } = useQuery({ queryKey: ["pyp-tests"], queryFn: () => get("/tests?test_type=pyp") });
  const { canTest } = useAccess();

  const yearOf = (t) => (t.title.match(/20\d{2}/) || [])[0] || "Other";
  const years = useMemo(() => {
    const counts = {};
    (data || []).forEach((t) => { const y = yearOf(t); counts[y] = (counts[y] || 0) + 1; });
    return Object.keys(counts).sort().reverse().map((y) => [y, counts[y]]);
  }, [data]);
  const shown = (data || []).filter((t) => year === "all" || yearOf(t) === year);

  const start = async (t) => {
    if (!canTest(t)) { toast.info("Your plan doesn't cover this exam — subscribe to unlock"); nav("/app/subscriptions"); return; }
    try { const r = await post(`/tests/${t.id}/start`); nav(`/exam/${r.attempt_id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Unavailable"); }
  };

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader title="Previous Year Papers" subtitle="Attempt actual previous-year papers, year by year" />
      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => setYear("all")} data-testid="pyp-year-all" className={`px-4 py-1.5 rounded-full text-sm font-semibold ${year === "all" ? "bg-slate-500 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>All ({(data || []).length})</button>
        {years.map(([y, c]) => (
          <button key={y} onClick={() => setYear(y)} data-testid={`pyp-year-${y}`} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${year === y ? "bg-slate-500 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{y} ({c})</button>
        ))}
      </div>
      {shown.length === 0 ? <EmptyState icon={ClipboardList} title="No previous year papers yet" /> : (
        <div className="space-y-3">
          {shown.map((t) => (
            <div key={t.id} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-wrap items-center gap-4" data-testid={`pyp-row-${t.id}`}>
              <div className="flex-1 min-w-[240px]">
                <h3 className="font-display font-bold text-slate-900">{t.title} {t.is_free && <Badge tone="green">Free</Badge>}</h3>
                <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-2">
                  <span className="flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> {t.total_questions} Questions</span>
                  <span className="flex items-center gap-1"><Trophy className="h-3.5 w-3.5" /> {t.total_marks} Marks</span>
                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {t.duration_min} Mins</span>
                </div>
              </div>
              {canTest(t)
                ? <button onClick={() => start(t)} data-testid={`start-test-${t.id}`} className="bg-indigo-800 hover:bg-indigo-900 text-white font-bold px-6 py-2.5 rounded-lg flex items-center gap-2"><Zap className="h-4 w-4" /> Start Now</button>
                : <button onClick={() => start(t)} data-testid={`unlock-test-${t.id}`} className="bg-sky-500 hover:bg-sky-600 text-white font-bold px-6 py-2.5 rounded-lg flex items-center gap-2"><Lock className="h-4 w-4" /> Unlock Now</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
